import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { apiService } from '../utils/api';
import useFormStore from "../state/useFormStore";
import { config } from '../config';
import { bankOptions } from '../data/selectOptions';

const Wrapper = styled.div`
  height: 100vh;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  margin: 0 36px;
  padding-bottom: 100px;
`;

const MainText = styled.div`
  margin: 30px 0 10px 0;
  font-size: 24px;
  line-height: 32px;
  font-weight: 600;
  color: #333;
  text-align: center;
`;
const Description = styled.p`
  font-size: 1rem;
  color: ${props => props.theme.colors.basic500};
  margin-bottom: 2rem;
  text-align: center;
  max-width: 400px;
  line-height: 1.5;
`;

const ErrorMessage = styled.span`
  color: red;
  font-size: 0.875rem;
  margin-top: -1rem;
  margin-bottom: 1rem;
  display: block;
`;

const Input = styled.input`
  width: 100%;
  max-width: 400px;
  padding: 1rem;
  border: 2px solid #95d0dd;
  border-radius: 8px;
  font-size: 1rem;
  margin-bottom: 1.5rem;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: #FFF200;
  }

  &:hover {
    border-color: #95d0dd;
  }

  &::placeholder {
    color: #666;
  }
`;

const Button = styled(motion.button)`
  background-color: #FFF200;
  color: black;
  font-weight: 600;
  box-shadow: none;
  border-radius: 50px;
  padding: 12px 40px;
  font-size: 16px;
  border: none;
  cursor: pointer;
  width: 100%;
  max-width: 400px;
  text-transform: none;

  &:disabled {
    background-color: #e0e0e0;
    cursor: not-allowed;
    color: #666;
  }

  &:hover:not(:disabled) {
    background-color: #FFE500;
  }
`;

const SkipButton = styled(Button)`
  background-color: transparent;
  color: #666;
  border: 1px solid #e0e0e0;
  margin-top: 1rem;

  &:hover:not(:disabled) {
    background-color: #f5f5f5;
  }
`;

const SkipLink = styled.button`
  background: none;
  border: none;
  color: #95d0dd;
  font-size: 14px;
  text-decoration: underline;
  cursor: pointer;
  padding: 0;
  margin-top: 1rem;
  font-family: "Figtree", serif;

  &:hover {
    color: #7ab8c5;
  }

  &:disabled {
    color: #ccc;
    cursor: not-allowed;
  }
`;

const SkipText = styled.p`
  font-size: 12px;
  color: #666;
  margin-top: 0.5rem;
  text-align: center;
  max-width: 400px;
  line-height: 1.4;
`;

const CBU = () => {
  const { hash } = useParams();
  const navigate = useNavigate();
  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState('');
  const [touched, setTouched] = useState(false);
  const { requestUid, setRequestUid } = useFormStore((state) => ({
    requestUid: state.requestUid,
    setRequestUid: state.setRequestUid
  }));
  const [loading, setLoading] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');

  useEffect(() => {
    if (!requestUid && hash) {
      const storedDataString = localStorage.getItem(`prestix_${hash}`);
      if (storedDataString) {
        try {
          const storedData = JSON.parse(storedDataString);
          if (storedData.requestUid) {
            setRequestUid(storedData.requestUid);
          }
        } catch (error) {
          console.error("Error al recuperar requestUid del localStorage:", error);
        }
      }
    }
  }, [hash, requestUid, setRequestUid]);

  useEffect(() => {
    if (hash) {
      const storedDataString = localStorage.getItem(`prestix_${hash}`);
      if (storedDataString) {
        const storedData = JSON.parse(storedDataString);
        setVerificationCode(storedData.bank);
      }
    }
  }, [hash]);

  // Función para capitalizar palabras (equivalente a ucwords de PHP)
  const capitalizeWords = (str: string): string => {
    return str.toLowerCase().split(' ').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  // Función para obtener el nombre del banco por código
  const getBankName = (bankCode: string): string => {
    const bank = bankOptions.find(option => option.value === bankCode);
    if (!bank) return 'Banco';
    
    const bankName = bank.label.toLowerCase();
    // Si el nombre ya contiene "banco" seguido de un espacio, no agregarlo
    if (bankName.includes('banco')) {
      return capitalizeWords(bankName);
    }
    // Si no contiene "banco " (con espacio), agregarlo
    return capitalizeWords(`Banco ${bankName}`);
  };

  const validateCBU = (cbu: string) => {
    const cleanCBU = cbu.replace(/\D/g, '');
    if (cleanCBU.length !== 22) {
      return 'El CBU debe tener exactamente 22 dígitos';
    }

    // Dividir el CBU en bloques
    const firstBlock = cleanCBU.substring(0, 8);
    const secondBlock = cleanCBU.substring(8, 22);

    // Validar el primer bloque
    const bankCode = firstBlock.substring(0, 3);
    const branchCode = firstBlock.substring(3, 7);
    const firstCheckDigit = parseInt(firstBlock.substring(7, 8));

    if (verificationCode != '' && bankCode != verificationCode){ // si en algún momento queremos validar el código de verificación para el banco
      const bankName = getBankName(verificationCode);
      return `El CBU debe pertenecer a tu cuenta de ${bankName}. Verifica que estés ingresando el CBU correcto`;
    }

    // Realizar la suma1 para el primer bloque
    const B1 = parseInt(bankCode[0]);
    const B2 = parseInt(bankCode[1]);
    const B3 = parseInt(bankCode[2]);
    const S1 = parseInt(branchCode[0]);
    const S2 = parseInt(branchCode[1]);
    const S3 = parseInt(branchCode[2]);
    const S4 = parseInt(branchCode[3]);

    const suma1 = B1 * 7 + B2 * 1 + B3 * 3 + S1 * 9 + S2 * 7 + S3 * 1 + S4 * 3;
    const diferencia1 = 10 - (suma1 % 10);

    const validFirstBlock = firstCheckDigit === (diferencia1 === 10 ? 0 : diferencia1);

    // Validar el segundo bloque
    const accountNumber = secondBlock.substring(0, 13);
    const secondCheckDigit = parseInt(secondBlock.substring(13, 14));

    // Realizar la suma2 para el segundo bloque
    const C1 = parseInt(accountNumber[0]);
    const C2 = parseInt(accountNumber[1]);
    const C3 = parseInt(accountNumber[2]);
    const C4 = parseInt(accountNumber[3]);
    const C5 = parseInt(accountNumber[4]);
    const C6 = parseInt(accountNumber[5]);
    const C7 = parseInt(accountNumber[6]);
    const C8 = parseInt(accountNumber[7]);
    const C9 = parseInt(accountNumber[8]);
    const C10 = parseInt(accountNumber[9]);
    const C11 = parseInt(accountNumber[10]);
    const C12 = parseInt(accountNumber[11]);
    const C13 = parseInt(accountNumber[12]);

    const suma2 = C1 * 3 + C2 * 9 + C3 * 7 + C4 * 1 + C5 * 3 + C6 * 9 + C7 * 7 + C8 * 1 + C9 * 3 + C10 * 9 + C11 * 7 + C12 * 1 + C13 * 3;
    const diferencia2 = 10 - (suma2 % 10);

    const validSecondBlock = secondCheckDigit === (diferencia2 === 10 ? 0 : diferencia2);

    // Retornar string vacío si es válido, o mensaje de error si no lo es
    return (validFirstBlock && validSecondBlock) ? '' : 'Ingrese un CBU válido';
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const numericValue = value.replace(/[^0-9]/g, '');
    
    if (numericValue.length <= 22) {
      setInputValue(numericValue);
      if (touched) {
        setError(validateCBU(numericValue));
      }
    }
  };

  const handleBlur = () => {
    setTouched(true);
    setError(validateCBU(inputValue));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTouched(true);
    
    if (!requestUid || !inputValue) {
      setError("Faltan datos para continuar.");
      return;
    }

    const validationError = validateCBU(inputValue);
    if (!validationError) {
      const cleanCbu = inputValue.replace(/\D/g, '');

      try {
        setLoading(true);
        await apiService.requests.updateCbu(requestUid, {
          cbu: cleanCbu
        });
        localStorage.setItem('userCBU', cleanCbu);
        navigate(`/${hash}/tarjeta`);
      } catch (error) {
        console.error("Error al enviar CBU:", error);
        setError("Error al enviar el CBU.");
      } finally {
        setLoading(false);
      }
    } else {
      setError(validationError);
    }
  };

  const handleSkip = async () => {
    if (!requestUid) {
      setError("Faltan datos para continuar.");
      return;
    }

    try {
      setLoading(true);
      localStorage.setItem('userCBU', 'SKIPPED');
      navigate(`/${hash}/tarjeta`);
    } catch (error) {
      console.error("Error al saltear CBU:", error);
      setError("Error al procesar la solicitud.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Wrapper>
      <MainText>Ingresá tu CBU</MainText>
      <Description>
        Recordá que debe tener 22 dígitos y estar a nombre del titular que solicita el crédito.
      </Description>
      <form onSubmit={handleSubmit} style={{ width: '100%', maxWidth: '400px' }}>
        <Input
          type="tel"
          inputMode="numeric"
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleBlur}
          placeholder="Ingresá tu CBU"
          required
          maxLength={22}
          pattern="[0-9]*"
          autoComplete="off"
          onPaste={(e) => {
            e.preventDefault();
            const pastedText = e.clipboardData.getData('text');
            const numericPastedText = pastedText.replace(/[^0-9]/g, '');
            if (numericPastedText.length <= 22) {
              setInputValue(numericPastedText);
              if (touched) {
                setError(validateCBU(numericPastedText));
              }
            }
          }}
          onKeyPress={(e) => {
            if (!/[0-9]/.test(e.key)) {
              e.preventDefault();
            }
          }}
        />
        {error && <ErrorMessage>{error}</ErrorMessage>}
        <Button
          type="submit"
          disabled={!inputValue.trim() || !!error}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          {loading ? "Cargando..." : "Continuar"}
        </Button>
        {config.environment.skipCbuVerification && (
          <>
            <SkipLink
              type="button"
              onClick={handleSkip}
              disabled={loading}
            >
              No sé mi CBU o necesito ayuda
            </SkipLink>
            <SkipText>
              Podés continuar y un analista te ayudará a cargar el CBU más tarde. Es necesario que lo tengas a mano para recibir el dinero.
            </SkipText>
          </>
        )}
      </form>
    </Wrapper>
  );
};

export default CBU; 